import { type ChangeEvent, type ReactElement, createContext, useContext, useState } from 'react';
import translations from '../../../translations';
import type {
    EditingPathInfo,
    OpenApiAPI,
    OpenApiSchemaInfo,
    OpenApiSecurityMe,
} from '../../../types/service';
import { isNullOrEmpty } from '../../../utils/guard';
import type { OAuth2GrantType } from '../../../types/identityProvider';

export type SetHasSubmitted = (state: boolean) => void;
export type SetSchemaInfo = (state: OpenApiSchemaInfo | null) => void;
export type SetOpenApiAPI = (state: OpenApiAPI | null) => void;
export type StringInputHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => void;
export type TextAreaInputHandler = ({
    target: { value },
}: ChangeEvent<HTMLTextAreaElement>) => void;
export type SetEditingPath = (path: EditingPathInfo | null) => void;
export type SaveEditingPath = () => void;

interface Props {
    children?: ReactElement;
}

interface OpenApiContext {
    hasSubmitted: boolean;
    setHasSubmitted: SetHasSubmitted;
    setSchemaInfo: SetSchemaInfo;
    schemaInfo: OpenApiSchemaInfo | null;
    onSecurityChange: (value: string) => void;
    onGrantTypeChange: (value: OAuth2GrantType) => void;
    onUserInfoUrlChange: StringInputHandler;
    onFirstNameChange: StringInputHandler;
    onLastNameChange: StringInputHandler;
    onEmailChange: StringInputHandler;
    onUsernameChange: StringInputHandler;
    onUserIdChange: StringInputHandler;
    onScopesChange: TextAreaInputHandler;
    setOpenApiAPI: SetOpenApiAPI;
    openApiAPI: OpenApiAPI | null;
    isSecurityValid: () => OpenApiSecurityValidationResult;
    editingPath: EditingPathInfo | null;
    setEditingPath: SetEditingPath;
    saveEditingPath: SaveEditingPath;
}

export interface OpenApiSecurityValidationResult {
    isValid: boolean;
    validationMessage: string;
}

const Context = createContext<OpenApiContext | undefined>(undefined);

const OpenApiConnectorProvider = (props: Props) => {
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [openApiAPI, setOpenApiAPI] = useState<OpenApiAPI | null>(null);
    const [schemaInfo, setSchemaInfo] = useState<OpenApiSchemaInfo | null>(null);
    const [editingPath, setEditingPath] = useState<EditingPathInfo | null>(null);

    const defaultMe: OpenApiSecurityMe = {
        userInfoUrl: '',
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        userId: '',
    };

    const onSecurityChange = (value: string) => {
        if (!schemaInfo) {
            return;
        }

        setHasSubmitted(false);

        if (value === 'OIDC' || value === 'NONE') {
            setSchemaInfo({ ...schemaInfo, security: value, me: defaultMe });
        } else {
            setSchemaInfo({ ...schemaInfo, security: value, scopes: '' });
        }
    };

    const onGrantTypeChange = (value: OAuth2GrantType) => {
        if (!schemaInfo) {
            return;
        }

        setHasSubmitted(false);

        if (value === 'ClientCredentials') {
            setSchemaInfo({ ...schemaInfo, grantType: value, me: defaultMe });
        } else {
            setSchemaInfo({ ...schemaInfo, grantType: value });
        }
    };

    const onScopesChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        if (!schemaInfo) {
            return;
        }
        setSchemaInfo({ ...schemaInfo, scopes: value });
    };

    const onUserInfoUrlChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), userInfoUrl: value },
        };
        setSchemaInfo(schema);
    };

    const onFirstNameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), firstName: value },
        };
        setSchemaInfo(schema);
    };

    const onLastNameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), lastName: value },
        };
        setSchemaInfo(schema);
    };

    const onEmailChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), email: value },
        };
        setSchemaInfo(schema);
    };

    const onUsernameChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), username: value },
        };
        setSchemaInfo(schema);
    };

    const onUserIdChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (!schemaInfo) {
            return;
        }
        const schema = {
            ...schemaInfo,
            me: { ...(schemaInfo.me ?? defaultMe), userId: value },
        };
        setSchemaInfo(schema);
    };

    const isSecurityValid = (): OpenApiSecurityValidationResult => {
        const validationResult = { isValid: true, validationMessage: '' };
        if (schemaInfo?.security === 'NONE') {
            validationResult.isValid = true;
        } else if (schemaInfo?.security === 'OIDC') {
            validationResult.isValid =
                !isNullOrEmpty(schemaInfo?.scopes) || schemaInfo.grantType === 'ClientCredentials';
        } else if (schemaInfo?.security === 'OAUTH2') {
            validationResult.isValid =
                !(
                    isNullOrEmpty(schemaInfo.me) ||
                    isNullOrEmpty(schemaInfo.me.userInfoUrl) ||
                    isNullOrEmpty(schemaInfo.me.firstName) ||
                    isNullOrEmpty(schemaInfo.me.lastName) ||
                    isNullOrEmpty(schemaInfo.me.username) ||
                    isNullOrEmpty(schemaInfo.me.email) ||
                    isNullOrEmpty(schemaInfo.me.userId)
                ) || schemaInfo?.grantType === 'ClientCredentials';
        } else if (schemaInfo?.security === 'BASIC') {
            //Either everything is filled or just the user url disallow half filled me mappings
            const justUrl =
                !isNullOrEmpty(schemaInfo.me) &&
                !isNullOrEmpty(schemaInfo.me.userInfoUrl) &&
                isNullOrEmpty(schemaInfo.me.firstName) &&
                isNullOrEmpty(schemaInfo.me.lastName) &&
                isNullOrEmpty(schemaInfo.me.username) &&
                isNullOrEmpty(schemaInfo.me.email) &&
                isNullOrEmpty(schemaInfo.me.userId);

            const allFilled =
                !isNullOrEmpty(schemaInfo.me) &&
                !(
                    isNullOrEmpty(schemaInfo.me.userInfoUrl) ||
                    isNullOrEmpty(schemaInfo.me.firstName) ||
                    isNullOrEmpty(schemaInfo.me.lastName) ||
                    isNullOrEmpty(schemaInfo.me.username) ||
                    isNullOrEmpty(schemaInfo.me.email) ||
                    isNullOrEmpty(schemaInfo.me.userId)
                );

            validationResult.isValid = allFilled || justUrl;

            if (!validationResult.isValid) {
                validationResult.validationMessage = translations.OPENAPI_security_basic_validation;
            }
        }

        return validationResult;
    };

    const saveEditingPath = () => {
        if (!schemaInfo?.paths || !editingPath) {
            return;
        }
        const schema = { ...schemaInfo };
        schema.paths[editingPath.index] = editingPath.path;
        setSchemaInfo(schema);
    };

    const contextValue: OpenApiContext = {
        hasSubmitted,
        setHasSubmitted,
        schemaInfo,
        setSchemaInfo,
        onSecurityChange,
        onGrantTypeChange,
        onFirstNameChange,
        onLastNameChange,
        onEmailChange,
        onUserIdChange,
        onUserInfoUrlChange,
        onUsernameChange,
        openApiAPI,
        setOpenApiAPI,
        onScopesChange,
        isSecurityValid,
        setEditingPath,
        editingPath,
        saveEditingPath,
    };

    return <Context.Provider value={contextValue}>{props.children}</Context.Provider>;
};

const useOpenApiConnector = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useOpenApiConnector must be used within a OpenApiConnectorProvider');
    }
    return context;
};

export { OpenApiConnectorProvider, useOpenApiConnector };
